import { BrowserRouter, Navigate, Route, Routes } from 'react-router'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { AuthProvider } from './contexts/AuthContext'
import { FeatureFlagProvider } from './contexts/FeatureFlagContext'
import { AppStateProvider } from './contexts/AppStateContext'
import { config } from './config/environment'
import ProtectedRoute from './components/ProtectedRoute'
import CardGate from './components/CardGate'
import AdminRoute from './components/AdminRoute'
import AppLayout from './components/AppLayout'
import { RouteErrorBoundary } from './components/ErrorBoundary'

import LoginPage from './pages/LoginPage'
import AuthRedirectPage from './pages/AuthRedirectPage'
import RegisterPage from './pages/RegisterPage'
import MfaVerifyPage from './pages/MfaVerifyPage'
import OAuthConfirmPage from './pages/OAuthConfirmPage'
import MfaRequiredPage from './pages/MfaRequiredPage'
import AuditorPortalPage from './pages/AuditorPortalPage'
import AdminAuditorsPage from './pages/admin/AdminAuditorsPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import AddCardGatePage from './pages/AddCardGatePage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import NoTeamPage from './pages/NoTeamPage'

import DashboardPage from './pages/DashboardPage'
import TemplatesPage from './pages/TemplatesPage'
import NewTemplatePage from './pages/NewTemplatePage'
import TemplateDetailPage from './pages/TemplateDetailPage'
import TemplatePreviewPage from './pages/TemplatePreviewPage'
import TemplateEditorPage from './pages/TemplateEditorPage'
import BlocksLibraryPage from './pages/BlocksLibraryPage'
import RenderJobsPage from './pages/RenderJobsPage'
import ApiKeysPage from './pages/ApiKeysPage'
import TeamPage from './pages/TeamPage'
import RolesPermissionsPage from './pages/RolesPermissionsPage'
import SettingsPage from './pages/SettingsPage'
import BillingPage from './pages/BillingPage'
import DemoPage from './pages/DemoPage'
import ApiDocsPage from './pages/ApiDocsPage'
import TemplateDocsPage from './pages/TemplateDocsPage'
import LandingPage from './pages/LandingPage'
import PricingPage from './pages/PricingPage'
import PublicLayout from './components/PublicLayout'
import SitePasswordGate from './components/SitePasswordGate'

import AuditLogPage from './pages/AuditLogPage'
import AccessReviewPage from './pages/AccessReviewPage'
import ConnectorsPage from './pages/ConnectorsPage'
import IntegrationsPage from './pages/IntegrationsPage'
import AiPage from './pages/AiPage'
import AnalyticsPage from './pages/AnalyticsPage'

import PlaygroundPage from './pages/PlaygroundPage'
import WarehousePage from './pages/WarehousePage'
import IngestionSourcesPage from './pages/IngestionSourcesPage'
import ESignPage from './pages/ESignPage'
import ESignRequestsPage from './pages/ESignRequestsPage'
import PromotionApprovalsPage from './pages/PromotionApprovalsPage'

import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import AdminUsersPage from './pages/admin/AdminUsersPage'
import AdminTeamsPage from './pages/admin/AdminTeamsPage'
import AdminBillingPage from './pages/admin/AdminBillingPage'
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage'
import AdminLogsPage from './pages/admin/AdminLogsPage'
import AdminCompliancePage from './pages/admin/AdminCompliancePage'
import AdminGovernancePage from './pages/admin/AdminGovernancePage'
import SecurityPage from './pages/SecurityPage'
import AdminJobsPage from './pages/admin/AdminJobsPage'
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage'

export default function App() {
  return (
    <GoogleOAuthProvider clientId={config.googleClientId}>
    <FeatureFlagProvider>
    <AuthProvider>
    <AppStateProvider>
    <SitePasswordGate>
      <BrowserRouter>
        <RouteErrorBoundary>
        <Routes>
          <Route path="/auth-redirect" element={<AuthRedirectPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/mfa" element={<MfaVerifyPage />} />
          <Route path="/sign-in/confirm" element={<OAuthConfirmPage />} />
          <Route path="/mfa-required" element={<MfaRequiredPage />} />
          <Route path="/audit" element={<AuditorPortalPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          {/* Public marketing pages — share PublicLayout header/footer */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/docs/api" element={<ApiDocsPage />} />
            <Route path="/docs/templates" element={<TemplateDocsPage />} />
          </Route>

          <Route path="/sign/:token" element={<ESignPage />} />

          <Route path="/app" element={<ProtectedRoute />}>
            {/* Outside CardGate so a card-gated team can actually reach the add-card step. */}
            <Route path="onboarding/add-card" element={<AddCardGatePage />} />
            <Route path="no-team" element={<NoTeamPage />} />
            <Route element={<CardGate />}>
            <Route element={<AppLayout />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="templates/new-text" element={<NewTemplatePage />} />
              <Route path="templates/:templateId" element={<TemplateDetailPage />} />
              <Route path="templates/:templateId/preview" element={<TemplatePreviewPage />} />
              <Route path="templates/:templateId/edit" element={<TemplateEditorPage />} />
              <Route path="blocks" element={<BlocksLibraryPage />} />
              <Route path="render-jobs" element={<RenderJobsPage />} />
              <Route path="esign" element={<ESignRequestsPage />} />
              <Route path="approvals" element={<PromotionApprovalsPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="roles-permissions" element={<RolesPermissionsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="access-review" element={<AccessReviewPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="ai" element={<AiPage />} />
              <Route path="playground" element={<PlaygroundPage />} />
              <Route path="warehouse" element={<WarehousePage />} />
              <Route path="ingestion" element={<IngestionSourcesPage />} />
              <Route path="connectors" element={<ConnectorsPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="billing" element={<BillingPage />} />

              <Route element={<AdminRoute />}>
                <Route path="admin" element={<AdminDashboardPage />} />
                <Route path="admin/users" element={<AdminUsersPage />} />
                <Route path="admin/teams" element={<AdminTeamsPage />} />
                <Route path="admin/analytics" element={<AdminAnalyticsPage />} />
                <Route path="admin/billing" element={<AdminBillingPage />} />
                <Route path="admin/notifications" element={<AdminNotificationsPage />} />
                <Route path="admin/logs" element={<AdminLogsPage />} />
                <Route path="admin/compliance" element={<AdminCompliancePage />} />
                <Route path="admin/governance" element={<AdminGovernancePage />} />
                <Route path="admin/jobs" element={<AdminJobsPage />} />
                <Route path="admin/auditors" element={<AdminAuditorsPage />} />
              </Route>
            </Route>
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RouteErrorBoundary>
      </BrowserRouter>
    </SitePasswordGate>
    </AppStateProvider>
    </AuthProvider>
    </FeatureFlagProvider>
    </GoogleOAuthProvider>
  )
}
